import { HistoricalTrueUpTableStats } from "features/fees/post/FeePostReviewBasisHistoricalTrueUpTab";
import {
  useFeeChargeEventRetrieveSuspense,
  useFeePeriodHistoricalTrueUpBreakdownRetrieveSuspense,
  useFeePeriodOffsetsTrueUpBreakdownRetrieveSuspense,
  useFeePeriodsAffectedByTrueUpRetrieveSuspense,
  useFeeTrueUpHistoricalPreviewStatsRetrieveSuspense,
} from "gen/endpoints/fee-charge-event/fee-charge-event";
import { FundFamilyEventTypeEnum } from "gen/model/fundFamilyEventTypeEnum";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectButton } from "shared/components/input/select/SelectButton";
import { SelectOption } from "shared/components/input/select/SelectOption";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const FeePeriodHistoricalBasisTrueUpBreakdownPage: React.FC = () => {
  const { t } = useTranslation(["fees"]);
  const { fundFamilyId, feeChargeEventId } = useRequiredStringParams();

  const { data: feeChargeEvent } = useFeeChargeEventRetrieveSuspense(
    fundFamilyId,
    feeChargeEventId,
  );

  const [feeChargeEventSelected, setFeeChargeEventSelected] = useState("");
  const { data: historicalTrueUpBreakdownData } =
    useFeePeriodHistoricalTrueUpBreakdownRetrieveSuspense(
      fundFamilyId,
      feeChargeEventId,

      {
        selected_fee_charge_event_id: feeChargeEventSelected,
      },
      {
        request: {
          // TODO Remove this once we speed up the endpoint
          timeout: 60_000,
        },
      },
    );

  const { data: historicalTrueUpOffsetsBreakdownData } =
    useFeePeriodOffsetsTrueUpBreakdownRetrieveSuspense(
      fundFamilyId,
      feeChargeEventId,
      {
        posted_fund_family_event_type: FundFamilyEventTypeEnum.charge_fee,
      },
      {
        request: {
          // TODO Remove this once we speed up the endpoint
          timeout: 60_000,
        },
      },
    );

  const { data: statsData } =
    useFeeTrueUpHistoricalPreviewStatsRetrieveSuspense(
      fundFamilyId,
      feeChargeEventId,
      {
        selected_fee_charge_event_id: feeChargeEventSelected,
      },
    );

  const { data: feePeriodSelectData } =
    useFeePeriodsAffectedByTrueUpRetrieveSuspense(
      fundFamilyId,
      feeChargeEventId,
    );

  const isFullWidth = true;

  return (
    <PageContent>
      <PageHeader
        title={t("fees:historicalBasisTrueUp")}
        backButton={{
          title: t("fees:feePostingWithPeriod", {
            period: feeChargeEvent.fee_charge_daterange_rich_name,
          }),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .fees({})
            .feeChargeEventPosted({ feeChargeEventId }).$,
        }}
      />
      <PageBody>
        <TableBox isFullWidth={isFullWidth}>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("fees:trueUpPages.trueUpBreakdown")}
            </TableBoxTitle>
            <DataTableBoxActions>
              <SelectButton
                size="sm"
                title={t("fees:trueUpPages.selectQuarter")}
                value={feeChargeEventSelected}
                onChangeValue={(value) => {
                  setFeeChargeEventSelected(value);
                }}
                variant="simple-table"
              >
                {feePeriodSelectData.map((element) => (
                  <SelectOption value={element.value} key={element.value}>
                    {element.rich_value}
                  </SelectOption>
                ))}
              </SelectButton>
            </DataTableBoxActions>
          </TableBoxHeader>
          <HistoricalTrueUpTableStats statsData={statsData} />
          <TableBoxBody>
            <DataTable data={historicalTrueUpBreakdownData} />
          </TableBoxBody>
        </TableBox>
        <TableBox isFullWidth={isFullWidth}>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("fees:trueUpPages.equalizedOffsets")}
            </TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={historicalTrueUpOffsetsBreakdownData} />
          </TableBoxBody>
        </TableBox>
      </PageBody>
    </PageContent>
  );
};
